import _ from 'underscore';
import {
  getAutoCroppedImages,
  constants,
  ImageExperienceImage,
  getCenterCroppedSettings,
} from '@bingads-webui-campaign-react/image-experience';
import { constants as C } from '@bingads-webui-campaign-react/image-experience-model';
import {
  ICropSettings,
  IImageExperienceImage,
  IImageMapUrl,
  IMTAsset,
  AssociationType,
  IImageAssetUpdate,
  IImageExperienceImageWithUpdate,
  IAssetPayload,
} from './types';
import { processImagePlacementsMetadata } from '../image-metadata-util';

const { subTypeToType } = constants;

const getImageProps = (
  asset: IMTAsset,
  imageToUrlMap: IImageMapUrl,
  options: {
    isIncluded?: boolean,
    isLogo?: boolean,
    cs?: ICropSettings
  },
  image: IImageExperienceImage
) => {
  const { cs, isIncluded, isLogo } = options;

  const idString = asset.IdString || String(asset.Id);
  const assetExtraInfo = imageToUrlMap[idString];

  let croppingType = asset.CroppingType;
  if (asset.SubType !== AssociationType.OriginalImage && !croppingType) {
    croppingType = C.CroppingType.ManualCrop;
  }

  let cropSettings = {
    SourceX: asset.CropX ? asset.CropX : 0,
    SourceY: asset.CropY ? asset.CropY : 0,
    SourceWidth: asset.CropWidth ? asset.CropWidth : assetExtraInfo.width,
    SourceHeight: asset.CropHeight ? asset.CropHeight : assetExtraInfo.height,
    CroppingType: croppingType,
  };

  // Recommeded image don't support auto crop
  if (assetExtraInfo.isRecommendedImage) {
    cropSettings = {
      SourceX: asset.CropX,
      SourceY: asset.CropY,
      SourceWidth: asset.CropWidth,
      SourceHeight: asset.CropHeight,
      CroppingType: '',
    };
  }

  return ({
    id: asset.Id,
    idString: String(asset.Id),
    baseId: imageToUrlMap[asset.Id].baseId,
    metadata: imageToUrlMap[asset.Id].metadata,
    src: imageToUrlMap[asset.Id].url,
    type: subTypeToType[asset.SubType],
    width: imageToUrlMap[asset.Id].width,
    height: imageToUrlMap[asset.Id].height,
    source: imageToUrlMap[asset.Id].sourceType,
    altText: asset.AltText,
    cropSettings,
    isIncluded,
    isLogo,
    originCropSetting: cs,
    isRecommendedImage: assetExtraInfo.isRecommendedImage,
    originalUrl: assetExtraInfo.originalUrl,
    cropSettingOnOriginal: assetExtraInfo.cropSettingOnOriginal,
    placementSettings: assetExtraInfo.placementSettings,
    originalWidth: assetExtraInfo.originalWidth,
    originalHeight: assetExtraInfo.originalHeight,
    associationType: image && image.associationType,
  });
};

const getMTAssetBaseId = (asset: IMTAsset, imageToUrlMap: IImageMapUrl) => {
  const info = imageToUrlMap[asset.Id];

  return info.baseId || asset.Id.toString();
};

/**
 * judge if the are using same base image
 * e.g. assets may come from base image and some effect
 */
const compareBaseImage = (
  image: IImageExperienceImage,
  asset: IMTAsset,
  imageToUrlMap: IImageMapUrl,
  isAdCreatorEnabled: boolean
): boolean => {
  const matchedId = isAdCreatorEnabled
    ? image.BaseId!.toString() === getMTAssetBaseId(asset, imageToUrlMap)
    : image.IdString === asset.IdString;

  return matchedId;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const generateMTAssetV2 = (payloadV1: IMTAsset, imageToUrlMap: IImageMapUrl): IMTAsset => {
  const payloadV2 = _.clone(payloadV1);
  payloadV2.SubType = 'OriginalImage';
  payloadV2.CropHeight = payloadV1.CropHeight;
  payloadV2.CropWidth = payloadV1.CropWidth;
  payloadV2.CropX = payloadV1.CropX;
  payloadV2.CropY = payloadV1.CropY;
  return payloadV2;
};

const generateMTAssetWithOriginalImage = (
  originalAssets: IMTAsset[],
  imageToUrlMap: IImageMapUrl
) => {
  // generate original image payload
  const assets: IMTAsset[] = [];
  const originalAssetsFor191x100AspectRatio = _.filter(originalAssets, asset => subTypeToType[asset.SubType] === AssociationType.Image191x100);

  _.each(originalAssetsFor191x100AspectRatio || [], (assetsV1) => {
    // add original image to payload
    assets.push(generateMTAssetV2(assetsV1, imageToUrlMap));
  });

  return assets;
};

/**
 * MMA In MSAN UI Dev Spec:
 * https://microsoft-my.sharepoint.com/:w:/g/personal/xiao_microsoft_com1/ESfoVZCPYKlFn5p0Ee_Xx-4ByJuBjdDMNQ7vl7CgotNScg?e=cLqZae
 * pseudo code for this:
 * https://microsoft-my.sharepoint.com/personal/xiao_microsoft_com1/_layouts/15/onedrive.aspx?id=%2Fpersonal%2Fxiao%5Fmicrosoft%5Fcom1%2FDocuments%2Fsample%2Ejs&parent=%2Fpersonal%2Fxiao%5Fmicrosoft%5Fcom1%2FDocuments
 * get the image associations to display for each image
 *
 * MMA V2 In MSAN UI Dev Spec:
 * to support more aspect ratios and use original image as default image.
 * https://microsoft-my.sharepoint.com/:w:/r/personal/youngim_microsoft_com/Documents/MMA%20V2%20DevSpec.docx?d=w145d99a164574567a3c7187597b204a8&csf=1&web=1&e=TBLlhW
 */
export const getMultiImages =
  (
    originalAssets: IMTAsset[],
    imageToUrlMap: IImageMapUrl,
    options: {
      isAdCreatorEnabled: boolean,
      isMMAV2Enabled?: boolean,
      isBoost200x100ImageEnabled?: boolean,
      isBoostNewAspectRatioSupportEnabled?: boolean,
      isImageFittingEnabled?: boolean,
    }
  ) => {
    let assets: IMTAsset[] = originalAssets;
    const isV2Format = options.isMMAV2Enabled && _.some(originalAssets, asset => (asset.SubType === AssociationType.OriginalImage));

    // this is true when editing an existing ad which is not in V2 format (at least has one original image)
    // when creating a new ad, the returned recommended ad are all in V2 format.
    const shouldConvertAssetToV2Format = options.isMMAV2Enabled && !_.some(originalAssets, asset => (asset.SubType === AssociationType.OriginalImage));
    if (shouldConvertAssetToV2Format) {
      assets = _.union(generateMTAssetWithOriginalImage(originalAssets, imageToUrlMap), originalAssets);
    }

    const imageGroupByType = _.groupBy(assets, (asset: IMTAsset) => subTypeToType[asset.SubType]);
    const multiImagesGroup = options.isAdCreatorEnabled
      ? _.uniq(_.map(assets, asset => getMTAssetBaseId(asset, imageToUrlMap)))
      : Object.keys(imageToUrlMap);

    const multiImages = multiImagesGroup.map((key) => {
      let imagesExperience: IImageExperienceImage[] = getAutoCroppedImages({
        // @ts-ignore
        originalImageToCrop: {
          Id: key,
          IdString: key,
          Src: imageToUrlMap[key].url,
          Width: imageToUrlMap[key].width,
          Height: imageToUrlMap[key].height,
          source: imageToUrlMap[key].sourceType,
        },
        isMultimediaAdinMSANEnabled: true,
        isRecommendedImage: imageToUrlMap[key].isRecommendedImage,
        isMMAV2Enabled: options.isMMAV2Enabled,
        isBoost200x100ImageEnabled: options.isBoost200x100ImageEnabled,
        isBoostNewAspectRatioSupportEnabled: options.isBoostNewAspectRatioSupportEnabled,
        setCroppingType: options.isImageFittingEnabled,
      });

      // @ts-ignore
      imagesExperience = imagesExperience.map((image) => {
        // find image association
        const asset: IMTAsset | undefined = _.find(assets, (mtAsset) => {
          const matchedType = subTypeToType[mtAsset.SubType] === image.Type;

          return matchedType && compareBaseImage(image, mtAsset, imageToUrlMap, options.isAdCreatorEnabled);
        });

        if (asset) {
          // @ts-ignore
          return new ImageExperienceImage(getImageProps(
            asset,
            imageToUrlMap,
            { isIncluded: true, isLogo: image.isLogo, cs: image.CropSettings },
            image
          ));
        }

        image.setIncluded(false);

        return image;
      });

      /* If default image (1.91 is the default image for legacy RA/MMA V1 AND OriginalImage is the default image for MMA V2)
       * is included, process other ratios which are not included with below rule:
       * --- When there are no associations for this aspect ratio in ad image associations, auto include this ratio.
       * --- unless the image fails size validation for this aspect ratio, then it will still be excluded (cannot be auto included).
       */
      const landscapeFound = getLandscapeImage(imagesExperience);
      const originalImageFound = options.isMMAV2Enabled && getOriginalImage(imagesExperience);

      if ((landscapeFound && landscapeFound.IsIncluded && !isV2Format)
        || (originalImageFound && originalImageFound.IsIncluded)) {
        _.each(imagesExperience, (image) => {
          // if no image association for this aspect ratio, then include the image.
          if (!image.IsIncluded && !imageGroupByType[image.Type]) {
            if (options.isAdCreatorEnabled) {
              image.copyEffectsFromOther(originalImageFound || landscapeFound);
            }

            image.setIncluded(true);
          }
        });
      }

      if (options.isAdCreatorEnabled) {
        processImagePlacementsMetadata(imagesExperience, key, imageToUrlMap[key], options.isImageFittingEnabled);
      }

      if (shouldConvertAssetToV2Format) {
        // check size validation before return
        // (in V2 aspect ratio contract, 1.69 size (min height 372) is bigger than default image (minheight 368))
        // it may be auto-included by the rule above (which is "When there are no associations for this aspect ratio in ad image associations, auto include this ratio")
        const aspectRatios = C.getAspectRatios({
          isMultimediaAd: true,
          isMultimediaAdinMSANEnabled: true,
          imageOption: C.defaultImageOption,
          isCombinedMsanAndMmaAspectRatios: false,
          isMMAV2Enabled: true,
          isBoost200x100ImageEnabled: options.isBoost200x100ImageEnabled,
          isBoostNewAspectRatioSupportEnabled: options.isBoostNewAspectRatioSupportEnabled,
        });
        _.each(imagesExperience, (image) => {
          const { minSize } = aspectRatios[image.Type];
          if (image.Type !== AssociationType.OriginalImage &&
            // original image is always included
            (image.CropSettings!.SourceWidth < minSize[0] || image.CropSettings!.SourceHeight < minSize[1])) {
            // Simulating the logic in XAP that if customer explicitly provided the image association for this aspect ratio and it's smaller than the requested ad placement size
            // XAP will drop this traffic directly, as a result, we exclude this image directly.
            // even if the src or default image is bigger than the minimum.
            image.setIncluded(false);
          }
        });
      }
      return imagesExperience;
    });

    if (shouldConvertAssetToV2Format) {
      // filter out the image with all aspect ratio excluded
      return _.filter(multiImages, imageExperience => _.some(imageExperience, image => image.IsIncluded));
    }

    return multiImages;
  };

const getLandscapeImage =
  (images: IImageExperienceImage[]): IImageExperienceImage => _.find(images, image => image.Type === AssociationType.Image191x100)!;

const getOriginalImage =
  (images: IImageExperienceImage[]): IImageExperienceImage => _.find(images, image => image.Type === AssociationType.OriginalImage)!;

// when nonLandscapeImageListOfOneType has same assets + auto crop settings, we treat the type as redundant info and will not send to MT
const checkIfShouldKeepImageType = (
  landscapeImageList: IImageExperienceImageWithUpdate[],
  nonLandscapeImageListOfOneType: IImageExperienceImageWithUpdate[],
  options: {
    isMMAV2Enabled: boolean,
    isBoost200x100ImageEnabled: boolean,
    isBoostNewAspectRatioSupportEnabled: boolean,
  }
) => {
  const landscapeSize = _.size(landscapeImageList);
  const sizeofThisType = _.size(nonLandscapeImageListOfOneType);

  if (landscapeSize !== sizeofThisType) { // when length not equal, keep this type
    return true;
  }

  // compare image content + crop settings
  const orderedLandscapeImageList =
    _.sortBy(landscapeImageList, 'finalIdString');
  const orderedNonLandscapeImageListOfOneType =
    _.sortBy(nonLandscapeImageListOfOneType, 'finalIdString');

  const aspectRatios = constants.getAspectRatios({
    isMultimediaAdinMSANEnabled: true,
    isMMAV2Enabled: options.isMMAV2Enabled,
    isBoost200x100ImageEnabled: options.isBoost200x100ImageEnabled,
    isBoostNewAspectRatioSupportEnabled: options.isBoostNewAspectRatioSupportEnabled,
  });
  const type = nonLandscapeImageListOfOneType[0].image.Type;
  const ratio = aspectRatios[type].aspectRatio;

  for (let i = 0; i < landscapeSize; i += 1) {
    const { finalIdString: landscapeImageFinalId, image: landscapeImage } = orderedLandscapeImageList[i];
    const { finalIdString: imageOfThisTypeId, image: imageOfThisType } = orderedNonLandscapeImageListOfOneType[i];

    if (landscapeImageFinalId !== imageOfThisTypeId) {
      return true;
    }

    // compare image crop settings:
    // instead of checking UI activities to decide if it is auto cropped or not, directly compare with center crop algorithm output.
    // In this way, we can decouple UI logic with mt/de related logic. This will help better maintain in the future.
    const autoCroppedSettings = getCenterCroppedSettings({
      // use full image as box to crop
      SourceWidth: landscapeImage.Width,
      SourceHeight: landscapeImage.Height,
      SourceX: 0,
      SourceY: 0,
    }, ratio);

    const isCropSettingsEqual = (cropSettings, originalCropSettings) => (
      cropSettings.SourceX === originalCropSettings.SourceX &&
      cropSettings.SourceY === originalCropSettings.SourceY &&
      cropSettings.SourceWidth === originalCropSettings.SourceWidth &&
      cropSettings.SourceHeight === originalCropSettings.SourceHeight
    );

    // for image fitting, if originalImageCropSettings is equal to CropSettings, then it is not cropped
    if ((!imageOfThisType.originalImageCropSettings && !isCropSettingsEqual(imageOfThisType.CropSettings, autoCroppedSettings)) ||
      (imageOfThisType.originalImageCropSettings && !isCropSettingsEqual(imageOfThisType.CropSettings, imageOfThisType.originalImageCropSettings)
        && !isCropSettingsEqual(imageOfThisType.CropSettings, autoCroppedSettings))) {
      return true;
    }
  }

  // if image looks + crop settings match, then not necessary to keep
  return false;
};

/**
 * MMA In MSAN UI Dev Spec:
 * https://microsoft-my.sharepoint.com/:w:/g/personal/xiao_microsoft_com1/ESfoVZCPYKlFn5p0Ee_Xx-4ByJuBjdDMNQ7vl7CgotNScg?e=cLqZae
 * https://microsoft-my.sharepoint.com/:w:/p/taxiahou/EYnHXWA0PPxNsABcWrfolBIB9HGCXO4OM7pjIKNGRF8sew
 * pseudo code for this:
 * https://microsoft-my.sharepoint.com/personal/xiao_microsoft_com1/_layouts/15/onedrive.aspx?id=%2Fpersonal%2Fxiao%5Fmicrosoft%5Fcom1%2FDocuments%2Fsample%2Ejs&parent=%2Fpersonal%2Fxiao%5Fmicrosoft%5Fcom1%2FDocuments
 * convert imageList to image asset from MT
 * @param imageList A flattened included assets list. Otherwise, it is UI state two dimension array.
 * @param imageIdsUpdatesByIndex Information on updated ids. In some cases such as applying effect, the new ids are saved here by position in imagelist.
 * @returns selected asset list that will be sent to MT
 */
export const getMMAPayloadV2 = (
  flattenedImageList: IImageExperienceImage[],
  imageIdsUpdatesByIndex: IImageAssetUpdate[],
  isMMAV2Enabled: boolean = false,
  isBoost200x100ImageEnabled: boolean = false,
  isBoostNewAspectRatioSupportEnabled: boolean = false,
  isImageFittingEnabled: boolean = false
): IAssetPayload[] => {
  // Motivation: If DE could not find association for a ratio, they will generate from landscape ratio 1.91
  const includedImageListByType = _.reduce(flattenedImageList, (memo, value, index) => {
    const key = value.Type;
    const overrideInfo = imageIdsUpdatesByIndex && imageIdsUpdatesByIndex[index];

    // eslint-disable-next-line no-param-reassign
    memo[key] = memo[key] || [];
    memo[key].push({
      finalIdString: ((overrideInfo && overrideInfo.id) || value.Id).toString(),
      image: value,
    });

    return memo;
  }, {}) as Record<AssociationType, IImageExperienceImageWithUpdate[]>;

  // default behavior: user cannot exclude one aspect ratio for all images in UI.
  // in MMA V2, if image size is smaller than 703x372 (default image is 703x368)
  // Aspect Ratio 1.69:1 will be excluded due to size validation
  // so we will add a validation here to check if we need to keep this type of image
  const aspectRatios = constants.getAspectRatios({
    isMultimediaAdinMSANEnabled: true,
    isMMAV2Enabled,
    isBoost200x100ImageEnabled,
    isBoostNewAspectRatioSupportEnabled,
  });
  const shouldIgnoreThisAlgorithm = isMMAV2Enabled && _.size(includedImageListByType) !== _.size(aspectRatios);

  const payload: IAssetPayload[] = [];

  _.each(includedImageListByType, (imageWithUpdateListOfThisType, type) => {
    let keep = false;

    // we only need to handle non-landscape ratios to check if they can be ignored
    const checkType = isMMAV2Enabled ? AssociationType.OriginalImage : AssociationType.Image191x100;
    if (type === checkType || shouldIgnoreThisAlgorithm) {
      keep = true;
    } else {
      keep =
        checkIfShouldKeepImageType(
          includedImageListByType[checkType],
          imageWithUpdateListOfThisType,
          {
            isMMAV2Enabled,
            isBoost200x100ImageEnabled,
            isBoostNewAspectRatioSupportEnabled,
          }
        );
    }

    if (keep) {
      const mtModelArr = imageWithUpdateListOfThisType.map(({
        finalIdString,
        image,
      }) => image.formatToSave(false, false, { id: parseInt(finalIdString, 10) || finalIdString }, isMMAV2Enabled, isImageFittingEnabled)); // recommended image is not existing asset, so it's id is image url string

      payload.push(...mtModelArr);
    }
  });

  return payload;
};
